import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { AuthenticationContext } from "./AuthenticationProvider";
import { useQuery } from "@tanstack/react-query";
import { baseUrl } from "components/shared/apiCrudBuilder";
import { useBackofficeConstant } from "./ConstantProvider";
import ModelViewModal from "components/ui/customs/generics/ModelViewModal";
import ModelUpdateModal from "components/ui/customs/generics/ModelUpdateModal";
import viewFields from "components/ui/customs/viewFields";
import useDidValueChange from "components/hooks/useDidValueChanged";

const path = `${baseUrl}/describe-all-serializers`;

const CustomContext = createContext({});

const getChoices = (field, constants) => {
  let choices = field.choices;
  if (!choices && field.subModel && constants[field.subModel]) {
    choices = constants[field.subModel].map((item) => ({
      value: item.id,
      label: item._title,
      obj: item,
    }));
  }
  if (choices && !Array.isArray(choices)) {
    console.warn(
      `Choices for field ${
        field.name
      } should be an array or a function returning an array, got ${typeof choices}`,
    );
    choices = [];
  }
  return choices;
};

const fieldAdapter = ({
  field,
  fieldExtraData,
  hiddenFields,
  disabledFields,
  constants,
  order,
}) => {
  let type = field.type;

  if (fieldExtraData.type) {
    if (viewFields[fieldExtraData.type]) {
      type = fieldExtraData.type;
    } else {
      console.warn(
        `Unknown internal type ${fieldExtraData.type} in field ${
          field.name
        }, possible values are: ${Object.keys(viewFields)}.`,
      );
    }
  } else {
    const fields = Object.entries(viewFields).filter(([nt, f]) =>
      f.djangoFields.includes(type),
    );
    if (fields.length > 0) {
      type = fields[0][0];
    } else {
      console.warn(
        `Can't find internal type in djangoFieldDico for ${field.type} in field ${field.name}`,
      );
    }
  }

  const choices = getChoices(field, constants);

  return {
    ...field,
    ...fieldExtraData,
    label: fieldExtraData.label || field.label || field.name,
    type,
    hidden:
      fieldExtraData.hidden ||
      (hiddenFields && hiddenFields.includes(field.name)) ||
      field.name.startsWith("_"), // ||
    //type === "primaryKeyRelatedField",
    disabled:
      fieldExtraData.disabled ||
      (disabledFields && disabledFields.includes(field.name)) ||
      field.read_only,
    sortable: fieldExtraData.sortable || type !== "listField",
    filterable: fieldExtraData.filterable || type !== "listField",
    choices,
    order: order,
  };
};

const modelAdapter = (model, extraData, constants) => {
  const { fields, hiddenFields = [], disabledFields = [] } = extraData || {};
  const orders = Object.fromEntries(
    (extraData?.fields_order || []).map((fieldName, index) => [
      fieldName,
      index,
    ]),
  );
  const adaptedFields = model.fields.map((field, index) => {
    const fieldExtraData = fields ? fields[field.name] || {} : {};
    return fieldAdapter({
      field,
      fieldExtraData,
      hiddenFields,
      disabledFields,
      constants,
      order:
        orders[field.name] === undefined ? index + 1000 : orders[field.name],
    });
  });

  return {
    viewModal: ModelViewModal(model.model),
    updateModal: ModelUpdateModal(model.model),
    ...model,
    ...extraData,
    fields: adaptedFields.sort((a, b) => a.order - b.order),
  };
};

const CustomProvider = ({ modelExtraData, children }) => {
  const constants = useBackofficeConstant();
  const { appAxiosRingback } = useContext(AuthenticationContext);

  const { data: models } = useQuery({
    queryKey: [`models`],
    queryFn: () => {
      return appAxiosRingback.get(path).then((res) => {
        const data = Object.fromEntries(
          res.data.map((serializer) => [serializer.model, serializer]),
        );
        return data;
      });
    },
    cacheTime: 1000 * 60 * 15, // 5 minutes
    staleTime: 1000 * 60 * 60, // 60 minutes
  });

  useDidValueChange(models, "models");
  useDidValueChange(modelExtraData, "modelExtraData");
  useDidValueChange(constants, "constants");

  const value = useMemo(() => {
    if (!models) return null;
    return {
      models: Object.fromEntries(
        Object.entries(models || []).map(([modelName, model]) => [
          modelName,
          modelAdapter(
            model,
            modelExtraData ? modelExtraData[modelName] : undefined,
            constants,
          ),
        ]),
      ),
    };
  }, [models, modelExtraData, constants]);

  if (!value) {
    return <div>Loading...</div>;
  }

  return (
    <CustomContext.Provider value={value}>{children}</CustomContext.Provider>
  );
};

export const useCustomModel = (modelName) => {
  const { models } = useContext(CustomContext);
  return models[modelName];
};

export default CustomProvider;
